import { DateTime } from 'luxon';

import { AdmissionPeriodType } from '@/enums/admissions';
import { ApplicationStatus } from '@/enums/applications';
import { AcademicYear } from '@/enums/users';
import type {
  AdmissionLifecyclePeriodModelType,
  AdmissionModelType,
} from '@/models/admissions';
import type { ApplicationModelType } from '@/models/applications';
import type { ProjectModelType } from '@/models/projects';
import type { UserModelType } from '@/models/users';
import type { Edge } from '@/types/shared';

export class AdmissionHelpers {
  static getAdmissionPeriodByType = (
    admission: AdmissionModelType,
    periodType: AdmissionPeriodType,
  ): AdmissionLifecyclePeriodModelType | undefined => {
    return admission.lifecyclePeriods?.edges
      .map((e: Edge<AdmissionLifecyclePeriodModelType>) => e.node)
      .find(
        (lp: AdmissionLifecyclePeriodModelType) => lp.periodType === periodType,
      );
  };

  static hasStudentApplicationPeriodStarted = (
    admission: AdmissionModelType,
  ): boolean => {
    const studentApplicationPeriod = AdmissionHelpers.getAdmissionPeriodByType(
      admission,
      AdmissionPeriodType.STUDENT_APPLICATION,
    );

    return (
      !!studentApplicationPeriod &&
      !!studentApplicationPeriod.start &&
      DateTime.fromISO(studentApplicationPeriod.start) < DateTime.now()
    );
  };

  static isPeriodActive = (
    admission: AdmissionModelType,
    periodType: AdmissionPeriodType,
  ): boolean => {
    const studentApplicationPeriod = AdmissionHelpers.getAdmissionPeriodByType(
      admission,
      periodType,
    );

    return (
      !!studentApplicationPeriod &&
      !!studentApplicationPeriod.start &&
      !!studentApplicationPeriod.end &&
      DateTime.fromISO(studentApplicationPeriod.start) < DateTime.now() &&
      DateTime.now() < DateTime.fromISO(studentApplicationPeriod.end)
    );
  };

  static isOneOfPeriodsActive = (
    admission: AdmissionModelType,
    periodTypes: AdmissionPeriodType[],
  ): boolean => {
    return periodTypes.some((pt) =>
      AdmissionHelpers.isPeriodActive(admission, pt),
    );
  };

  static isPeriodRangeActive = (
    admission: AdmissionModelType,
    periodFromType: AdmissionPeriodType,
    periodToType: AdmissionPeriodType,
  ): boolean => {
    const periodFrom = AdmissionHelpers.getAdmissionPeriodByType(
      admission,
      periodFromType,
    );

    const periodTo = AdmissionHelpers.getAdmissionPeriodByType(
      admission,
      periodToType,
    );

    return (
      !!periodFrom?.start &&
      !!periodTo?.end &&
      DateTime.fromISO(periodFrom.start) < DateTime.now() &&
      DateTime.now() < DateTime.fromISO(periodTo.end)
    );
  };

  static isMentorOfProject = (
    user: UserModelType | undefined,
    project: ProjectModelType,
  ) => {
    return project.mentors?.edges.some(
      (e: Edge<UserModelType>) => e.node.pk === user?.pk,
    );
  };
}

export class UserHelpers {
  static formatStudentUniversities = (user: UserModelType): string => {
    return (
      user.studentprofile?.universities?.edges
        .map((e) => e.node)
        .map((u) =>
          [
            u.university?.shortName || u.university?.name,
            u.courseOfStudy,
            AcademicYear[u.academicYear],
            u.graduationYear,
          ]
            .filter(Boolean)
            .join(', '),
        )
        .join(';\n') || ''
    );
  };

  static formatProjectMentors = (
    project: ProjectModelType,
  ): string | undefined => {
    return project.mentors?.edges
      .map((e) => e.node)
      .map((m: UserModelType) =>
        [m.firstName, m.lastName].filter(Boolean).join(' '),
      )
      .filter(Boolean)
      .join(', ');
  };
}

export class ApplicationHelpers {
  /**
   * Calculates the deadline for an application based on the project time limit
   * and the application start time. Requires the application to be in IN_PROGRESS status.
   */
  static getDeadline = (
    application: ApplicationModelType,
    project?: ProjectModelType,
  ): DateTime => {
    if (application.status != ApplicationStatus.IN_PROGRESS) {
      throw new Error(
        '[ApplicationHelpers.getDeadline] Application is not in IN_PROGRESS state',
      );
    }
    const effectiveProject = project ?? application.project;
    if (!effectiveProject) {
      throw new Error('[ApplicationHelpers.getDeadline] Cannot get project');
    }
    if (!application.startedAt) {
      throw new Error('[ApplicationHelpers.getDeadline] startedAt is missing');
    }
    return DateTime.fromISO(application.startedAt).plus({
      minutes: effectiveProject.testTaskTime?.value,
    });
  };

  /**
   * Requires the application to be in IN_PROGRESS status.
   */
  static deadlineExceeded = (
    application: ApplicationModelType,
    project?: ProjectModelType,
  ): boolean => {
    const deadline = ApplicationHelpers.getDeadline(application, project);
    return deadline < DateTime.now();
  };
}
